import { ArrowLeft, Droplets, Info } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import {
  ACID_BASE_CONDITIONS,
  ACID_BASE_MODEL_BOUNDARIES,
  ACID_BASE_MODE_YEARS,
  ACID_BASE_MODES,
  ACID_BASE_SOURCES,
  type AcidBaseMode,
} from './acidBaseCalculations';
import { AcidBaseCalculationsExplore } from './AcidBaseCalculationsExplore';
import { AcidBaseCalculationsProblems } from './AcidBaseCalculationsProblems';
import { AcidBaseCalculationsTheory } from './AcidBaseCalculationsTheory';
import { StudyShell } from './StudyShell';
import { toolsHubPath } from './studyTools';
import { modeForYear, useChemistryToolYear } from './useChemistryToolYear';

const MODE_COPY: Record<
  AcidBaseMode,
  { tab: string; eyebrow: string; title: string; description: string }
> = {
  theory: {
    tab: 'Acid–base theory',
    eyebrow: 'Brønsted–Lowry',
    title: 'Who gives the proton?',
    description:
      'Label the acid, base and conjugate partners in each reaction. A conjugate pair differs by one H⁺.',
  },
  ph: {
    tab: 'Strong solutions: pH',
    eyebrow: 'pH, pOH, Kw',
    title: 'Strong acids and bases: pH = −log₁₀[H₃O⁺]',
    description:
      'Convert between [H₃O⁺], [OH⁻], pH and pOH, then handle dilution and mixing an acid with a base.',
  },
  weak: {
    tab: 'Weak acids and bases',
    eyebrow: 'Ka, Kb, pKa',
    title: 'Weak acids and bases: Ka = [H₃O⁺][A⁻] ÷ [HA]',
    description:
      'Use Ka or Kb to find pH and percentage ionisation. Test the 5 % rule before you trust the shortcut.',
  },
};

export function AcidBaseCalculationsLabPage() {
  const year = useChemistryToolYear();
  const [mode, setMode] = useState<AcidBaseMode>(() => modeForYear(ACID_BASE_MODE_YEARS, year));
  const [view, setView] = useState<'explore' | 'practice'>('explore');
  const copy = MODE_COPY[mode];

  return (
    <StudyShell scienceTutor={{ subjectKey: 'chemistry', toolId: 'acid-base-calculations', state: { mode } }}>
      <Link
        to={toolsHubPath('chemistry')}
        className="inline-flex items-center gap-2 text-sm font-extrabold text-slate2 hover:text-ink"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden="true" />
        Chemistry tools
      </Link>

      <header className="chemistry-tool-header mt-5 flex flex-col gap-5 rounded-[2rem] border border-brand-coral/25 bg-gradient-to-br from-wash-coral via-canvas-pure to-wash-sky p-6 shadow-card-soft lg:flex-row lg:items-end lg:justify-between lg:p-8">
        <div className="max-w-3xl">
          <p className="flex items-center gap-2 text-xs font-extrabold uppercase tracking-[0.16em] text-brand-coral">
            <Droplets className="h-4 w-4" aria-hidden="true" /> Acids, ions &amp; electrochemistry
          </p>
          <h1 className="mt-3 text-3xl font-extrabold tracking-tight text-ink sm:text-4xl">
            Acid–Base Calculations
          </h1>
          <p className="mt-3 max-w-2xl font-medium leading-7 text-slate2">
            Follow the proton, then put numbers on it. Predict each answer, check it against a
            deterministic model and see where it sits on the pH scale.
          </p>
        </div>
        <div className="rounded-2xl border border-white/80 bg-white/75 px-4 py-3 text-sm font-bold text-ink-soft">
          Explore tool · no mastery claim
        </div>
      </header>

      <div
        className="mt-6 flex max-w-full flex-wrap gap-1 rounded-[1.5rem] border border-hairline bg-canvas-pure p-1 shadow-card-soft sm:inline-flex sm:rounded-full"
        role="tablist"
        aria-label="Acid–base topic"
      >
        {ACID_BASE_MODES.map((candidate) => (
          <button
            key={candidate}
            type="button"
            role="tab"
            aria-selected={mode === candidate}
            onClick={() => setMode(candidate)}
            className={`rounded-full px-4 py-2.5 text-sm font-extrabold ${mode === candidate ? 'bg-ink text-white' : 'text-slate2'}`}
          >
            {MODE_COPY[candidate].tab}
          </button>
        ))}
      </div>

      <div className="mt-6">
        <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-brand-coral">
          {copy.eyebrow}
        </p>
        <h2 className="mt-1 text-2xl font-extrabold text-ink">{copy.title}</h2>
        <p className="mt-1 font-medium text-slate2">{copy.description}</p>
        <p className="mt-2 text-sm font-bold text-ink-soft">{ACID_BASE_CONDITIONS}</p>
      </div>

      <div key={mode} className="mt-5">
        {mode === 'theory' ? (
          <AcidBaseCalculationsTheory />
        ) : (
          <>
            <div
              className="mb-5 inline-flex gap-1 rounded-full border border-hairline bg-canvas-pure p-1"
              role="tablist"
              aria-label="Explore or practise"
            >
              {(['explore', 'practice'] as const).map((candidate) => (
                <button
                  key={candidate}
                  type="button"
                  role="tab"
                  aria-selected={view === candidate}
                  onClick={() => setView(candidate)}
                  className={`rounded-full px-4 py-2 text-sm font-extrabold ${view === candidate ? 'bg-ink text-white' : 'text-slate2'}`}
                >
                  {candidate === 'explore' ? 'Explore' : 'Practice'}
                </button>
              ))}
            </div>
            {view === 'explore' ? <AcidBaseCalculationsExplore mode={mode} /> : <AcidBaseCalculationsProblems mode={mode} />}
          </>
        )}
      </div>

      <section className="mt-6 rounded-[1.5rem] border border-hairline bg-surface p-5 text-sm font-medium leading-6 text-slate2">
        <p className="flex items-center gap-2 font-extrabold text-ink">
          <Info className="h-4 w-4" aria-hidden="true" /> Model boundaries and sources
        </p>
        <ul className="mt-2 list-disc space-y-1 pl-5">
          {ACID_BASE_MODEL_BOUNDARIES.map((line) => (
            <li key={line}>{line}</li>
          ))}
          <li>Progress is not saved. Values are pending teaching review.</li>
        </ul>
        <p className="mt-3">
          Sources:{' '}
          {ACID_BASE_SOURCES.map((source) => `${source.label} — ${source.detail}`).join(' ')}
        </p>
      </section>
    </StudyShell>
  );
}
